/* @bend-expect quartz-no-create-browser-history */
import { createBrowserHistory } from './react-router';
import { createNestedHistory } from './createNestedHistory';
import { get, set } from 'quartz/globalState/api';
import { getHistory } from './service';
import { HISTORY_INSTANCE, HISTORY_BASE_PATH, GRANITE_NAVIGATE } from './globalState';
function matchPattern(pattern, pathname) {
  if (pattern === '/') {
    if (pathname.startsWith('/')) {
      return {
        basePathname: '/',
        params: {}
      };
    }
    return undefined;
  }
  const patternSegments = pattern.split('/');
  const pathSegments = pathname.split('/');
  if (pathSegments.length < patternSegments.length) {
    return undefined;
  }
  const params = {};
  const matchedSegments = [];
  for (let i = 0; i < patternSegments.length; i++) {
    const patternSegment = patternSegments[i];
    const pathSegment = pathSegments[i];
    if (patternSegment.startsWith(':')) {
      const paramName = patternSegment.slice(1);
      if (!paramName) {
        throw new TypeError(`Invalid historyBasename pattern '${pattern}'. Missing parameter name.`);
      }
      if (!pathSegment) {
        return undefined;
      }
      params[paramName] = pathSegment;
      matchedSegments.push(pathSegment);
    } else if (patternSegment === pathSegment) {
      matchedSegments.push(pathSegment);
    } else {
      return undefined;
    }
  }
  return {
    basePathname: matchedSegments.join('/'),
    params
  };
}
export function findBasePath(historyBasename, currentPathname) {
  const basenames = Array.isArray(historyBasename) ? historyBasename : [historyBasename];
  for (const pattern of basenames) {
    const match = matchPattern(pattern, currentPathname);
    if (match) {
      return match;
    }
  }
}
export function initializeNestedHistory(historyBasename, attachHistory) {
  const basePath = findBasePath(historyBasename, window.location.pathname);
  if (!basePath) {
    throw new Error('Browser url does not match `historyBasename`');
  }
  const relativePath = window.location.pathname.slice(basePath.basePathname.length) || '/';
  const history = createNestedHistory(basePath.basePathname, relativePath, window.location.search);
  set(HISTORY_INSTANCE, history);
  set(HISTORY_BASE_PATH, basePath);
  attachHistory(history, basePath.basePathname);
  return history;
}
export function syncHistoryToBrowser(remoteHistory, browserHistory = window.history) {
  return remoteHistory._connect(browserHistory);
}
export function initializeHistory(historyBasename) {
  const basePath = findBasePath(historyBasename, window.location.pathname);
  if (!basePath) {
    throw new Error('Browser url does not match `historyBasename`');
  }
  const history = createBrowserHistory({
    basename: basePath.basePathname
  });
  set(HISTORY_INSTANCE, history);
  set(HISTORY_BASE_PATH, basePath);
  return history;
}
function pathnameOf(to) {
  const end = to.search(/[?#]/);
  return end === -1 ? to : to.slice(0, end);
}
function isWithinBasename(pathname, basePathname) {
  if (basePathname === '' || basePathname === '/') {
    return true;
  }
  return pathname === basePathname || pathname.startsWith(`${basePathname}/`);
}

// A prefix check isn't enough: browsers normalise `/\host` and `/\/host` to the
// protocol-relative cross-origin URL `//host`, so resolve through URL and
// require the result stays same-origin.
function isSiteAbsolutePath(to) {
  if (!to.startsWith('/')) {
    return false;
  }
  try {
    return new URL(to, window.location.origin).origin === window.location.origin;
  } catch (_unused) {
    return false;
  }
}

// Full-page nav seam (mirrors HistoryManager._graniteNav) so tests can assert a
// document load without the browser leaving the page.
export const _routingNav = {
  fullPageNavigate(url) {
    window.location.assign(url);
  }
};

/**
 * Navigate to a same-origin, site-absolute path (e.g. '/crm/123/contacts'),
 * crossing app boundaries when needed. Use instead of getHistory().push() when
 * the target may live in another app: an app's history only routes within its
 * own basename. Throws on cross-origin or non-absolute input.
 */
export function navigate(to) {
  if (!isSiteAbsolutePath(to)) {
    throw new TypeError(`navigate() requires a same-origin, site-absolute path like '/crm/123'; got '${to}'`);
  }
  const graniteNavigate = get(GRANITE_NAVIGATE);
  if (graniteNavigate) {
    graniteNavigate(to);
    return;
  }
  const pathname = pathnameOf(to);
  const suffix = to.slice(pathname.length);
  const basePath = get(HISTORY_BASE_PATH);
  if (basePath && !isWithinBasename(pathname, basePath.basePathname)) {
    _routingNav.fullPageNavigate(to);
    return;
  }

  // Push the basename-relative remainder so the app history routes it.
  const relative = basePath && basePath.basePathname !== '/' ? pathname.slice(basePath.basePathname.length) || '/' : pathname;
  getHistory().push(relative + suffix);
}